import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Link, useHistory } from 'react-router-dom'
import Header from '../Component/Header'
import Footer from '../Component/footer'
import PageHead from '../Component/pageHead'
import { PostAction } from '../Component/PostAction'
import { useFormik } from 'formik'
import {
  applyLoginResponse,
  changePasswordPath,
  forgotPasswordPath,
  loginPath,
  markDonationResumeCheckout,
  needsPasswordChange,
} from '../utils/sessionAuth'

function normalizeIndianMobile(value) {
  let digits = String(value || '').replace(/\D/g, '')
  if (digits.length === 13 && digits.startsWith('091')) {
    digits = digits.slice(3)
  } else if (digits.length === 12 && digits.startsWith('91')) {
    digits = digits.slice(2)
  } else if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1)
  }
  return /^[6-9]\d{9}$/.test(digits) ? digits : ''
}

const GUEST_EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  password: '',
  confirmPassword: '',
  panNumber: '',
  address1: '',
  createAccount: false,
}

const LOGIN_EMPTY = {
  username: '',
  password: '',
}

const RETURN_URL = '/Donation'

function GuestCheckout() {
  const history = useHistory()
  const [saving, setSaving] = useState(false)
  const [loggingIn, setLoggingIn] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem('id') && sessionStorage.getItem('is_guest') !== '1') {
      history.replace(RETURN_URL)
    }
  }, [history])

  const finishToPayment = () => {
    markDonationResumeCheckout()
    history.push(RETURN_URL)
  }

  const loginForm = useFormik({
    initialValues: LOGIN_EMPTY,
    onSubmit: async (loginValues) => {
      setLoggingIn(true)
      const res = await PostAction(
        'post',
        {
          username: String(loginValues.username || '').trim(),
          password: loginValues.password,
        },
        'user/loginCheck'
      )
      setLoggingIn(false)
      if (!applyLoginResponse(res)) {
        alert(res?.message || res?.data?.message || 'Login Failed!')
        return
      }
      if (needsPasswordChange()) {
        history.push(changePasswordPath(RETURN_URL))
        return
      }
      finishToPayment()
    },
  })

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: GUEST_EMPTY,
    onSubmit: (submitValues) => {
      const firstName = String(submitValues.firstName || '').trim()
      const lastName = String(submitValues.lastName || '').trim()
      const email = String(submitValues.email || '').trim()
      const mobileRaw = String(submitValues.mobile || '').trim()
      const mobile = mobileRaw ? normalizeIndianMobile(mobileRaw) : ''
      const password = String(submitValues.password || '')
      const confirmPassword = String(submitValues.confirmPassword || '')
      const address1 = String(submitValues.address1 || '').trim()
      const createAccount = Boolean(submitValues.createAccount)

      if (!firstName || !lastName) {
        alert('Please enter your first and last name.')
        return
      }
      if (createAccount) {
        if (!email && !mobile) {
          alert('Please enter an email address or a mobile number.')
          return
        }
      } else if (!email) {
        alert('Please enter an email address to receive your donation receipt.')
        return
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        alert('Please enter a valid email address.')
        return
      }
      if (mobileRaw && !mobile) {
        alert('Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.')
        return
      }
      if (!address1) {
        alert('Please enter your address.')
        return
      }
      if (createAccount) {
        if (!password) {
          alert('Please enter a password.')
          return
        }
        if (password !== confirmPassword) {
          alert('Password and confirm password do not match.')
          return
        }
      }

      const payload = {
        firstName,
        lastName,
        email,
        mobile,
        panNumber: submitValues.panNumber,
        address1,
      }

      const submit = async () => {
        setSaving(true)
        if (createAccount) {
          const res = await PostAction(
            'post',
            { ...payload, password },
            'user/signupUser'
          )
          if (res?.status !== 'success') {
            setSaving(false)
            alert(res?.message || res?.data?.message || 'Sign up failed.')
            return
          }

          const loginRes = await PostAction(
            'post',
            { username: email || mobile, password },
            'user/loginCheck'
          )
          setSaving(false)
          if (applyLoginResponse(loginRes)) {
            finishToPayment()
            return
          }
          alert('Account created. Please log in.')
          history.push(loginPath(RETURN_URL))
          return
        }

        const guestRes = await PostAction('post', payload, 'user/prepareGuestDonor')
        setSaving(false)
        if (!applyLoginResponse(guestRes)) {
          alert(
            guestRes?.message ||
              guestRes?.data?.message ||
              'Unable to continue. Please try again.'
          )
          return
        }
        finishToPayment()
      }
      submit()
    },
  })

  return (
    <>
      <Header />
      <PageHead title="Continue donation" />
      <section className="donation-page">
        <div className="container">
          <div className="donation-card mb-4">
            <div className="donation-card-header">
              <h2 className="donation-card-title">Login</h2>
              <p className="donation-card-subtitle">
                Already have an account? Log in to continue your donation.
              </p>
            </div>
            <Form onSubmit={loginForm.handleSubmit} className="donation-form">
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="guest-login-username">
                    Email or mobile
                  </label>
                  <input
                    id="guest-login-username"
                    className="form-control"
                    type="text"
                    name="username"
                    value={loginForm.values.username}
                    onChange={loginForm.handleChange}
                    autoComplete="username"
                    placeholder="email@example.com or 10-digit mobile"
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="guest-login-password">
                    Password
                  </label>
                  <input
                    id="guest-login-password"
                    className="form-control"
                    type="password"
                    name="password"
                    value={loginForm.values.password}
                    onChange={loginForm.handleChange}
                    autoComplete="current-password"
                  />
                  <div className="text-end mt-1">
                    <Link to={forgotPasswordPath(RETURN_URL)} style={{ fontSize: 14 }}>
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </div>
              <div className="donation-actions">
                <button className="btn donation-submit-btn" type="submit" disabled={loggingIn}>
                  {loggingIn ? 'Logging in…' : 'Login'}
                </button>
              </div>
            </Form>
          </div>

          <div className="donation-card">
            <div className="donation-card-header">
              <h2 className="donation-card-title">Guest checkout</h2>
              <p className="donation-card-subtitle">
                Continue without logging in. Enter your details below.
                {values.createAccount
                  ? ' Enter an email address, a mobile number, or both. At least one is required.'
                  : ' Email is required so we can send your donation receipt.'}
                {' '}
                Check Create my account to register and stay logged in for future donations.
              </p>
            </div>

            <Form onSubmit={handleSubmit} className="donation-form">
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="firstName">
                    First name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="firstName"
                    id="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="lastName"
                    id="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="email">
                    Email{!values.createAccount ? ' *' : ''}
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    autoComplete="email"
                    placeholder="name@example.com"
                  />
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label" htmlFor="mobile">
                    Mobile
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="mobile"
                    id="mobile"
                    value={values.mobile}
                    onChange={handleChange}
                    autoComplete="tel"
                    inputMode="numeric"
                    placeholder="10-digit mobile"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="panNumber">
                  PAN Number <span className="text-muted">(Optional)</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="panNumber"
                  id="panNumber"
                  value={values.panNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="address1">
                  Address
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="address1"
                  id="address1"
                  value={values.address1}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="createAccount"
                  name="createAccount"
                  checked={Boolean(values.createAccount)}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setFieldValue('createAccount', checked)
                    if (!checked) {
                      setFieldValue('password', '')
                      setFieldValue('confirmPassword', '')
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="createAccount">
                  Create my account
                </label>
              </div>

              {values.createAccount ? (
                <div className="row">
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label" htmlFor="password">
                      Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      name="password"
                      id="password"
                      value={values.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label" htmlFor="confirmPassword">
                      Confirm password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      name="confirmPassword"
                      id="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              ) : null}

              <div className="donation-actions">
                <button className="btn donation-submit-btn" type="submit" disabled={saving}>
                  {saving
                    ? values.createAccount
                      ? 'Creating account…'
                      : 'Continuing…'
                    : values.createAccount
                      ? 'Sign Up & Continue'
                      : 'Proceed'}
                </button>
              </div>
            </Form>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default GuestCheckout
